import {
  Component,
  HostListener,
  ElementRef,
  ViewChild,
  computed,
  signal,
  Input,
  OnChanges,
  OnDestroy,
  AfterViewInit,
  SimpleChanges,
  effect,
  Output,
  EventEmitter,
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { Router, RouterLink, NavigationEnd } from '@angular/router';
import { filter, Observable, of, map, catchError } from 'rxjs';
import { NotificationsPanelComponent } from '../notifications-panel/notifications-panel.component';
import { AuthService } from '../../../auth/services/auth.service';
import { SelectedLegalEntityService } from '../../../../shared/services/selected-legal-entity.service';
import {
  NotificationService,
  NotificationDto,
  NOTIFICATION_TYPES,
} from '../../../notifications/notification.service';
import { LoggerService } from '../../../logging/logger.service';
import { NotificationStateService } from '../../../notifications/notification-state.service';
import { AzTagComponent } from '../../../../shared/components/az-tag/az-tag.component';
import { AzDropdownComponent } from '../../../../shared/components/az-dropdown/az-dropdown.component';
import { StatusSplitDropdownComponent } from '../../../../shared/components/status-split-dropdown/status-split-dropdown.component';
import { SignatoryService } from '../../../../features/signatory-list/services/signatory.service';
import { StatusChangeModalComponent } from '../../../../shared/components/status-change-modal/status-change-modal.component';
import {
  LegalEntityService,
  BcrStatusDto,
} from '../../../../features/legal-entity-list/services/legal-entity.service';
import { AuthLike, AuthTokenPayload } from '../../../auth/models/auth.types';
import {
  StatusChangeModalData,
  StatusChangeModalState,
  PendingStatusChange,
  ApiResponse,
  RouteTitleMap,
} from './toolbar.types';

@Component({
  selector: 'app-toolbar',
  standalone: true,
  imports: [
    MatIconModule,
    CommonModule,
    RouterLink,
    NotificationsPanelComponent,
    AzTagComponent,
    AzDropdownComponent,
    StatusSplitDropdownComponent,
    StatusChangeModalComponent,
  ],
  templateUrl: './toolbar.component.html',
  styleUrl: './toolbar.component.scss',
})
export class ToolbarComponent implements OnChanges, AfterViewInit, OnDestroy {
  statusMenuOpen = false;
  notificationsOpen = false;
  accountPanelOpen = false;

  private bcrStatusCache: BcrStatusDto[] = [];

  // Expose notification state from service
  get notifications() {
    return this.notificationState.notifications;
  }
  get hasUnreadNotifications() {
    return this.notificationState.hasUnreadNotifications;
  }
  get notificationsLoading() {
    return this.notificationState.isLoading();
  }

  // Status Change Modal
  showStatusChangeModal = false;
  currentNotification: NotificationDto | null = null;
  statusChangeModalData: StatusChangeModalData = {
    legalEntityName: '',
    currentStatus: '',
    newStatus: '',
    alreadyProcessedBy: '',
  };
  statusChangeModalInitialState: StatusChangeModalState = 'confirm-change';

  // Store pending status change details for confirmation
  pendingStatusChange: PendingStatusChange | null = null;

  statusChangeModalTitle = 'Status Change';
  statusChangeModalProcessingTitle = 'Loading...';

  @ViewChild('statusChangeModal') statusChangeModal?: StatusChangeModalComponent;
  @ViewChild('toolbarWrapper') toolbarWrapperEl?: ElementRef<HTMLElement>;
  private resizeObserver?: ResizeObserver;

  /** Status items are provided by parent (legal entity list). Parent may omit; keep small fallback. */
  @Input() statusDescriptions: string[] = [
    'Pending Letter of Adherence',
    'Approved Letter of Adherence',
    'Rejected Letter of Adherence',
    'In Review',
  ];
  /** Currently selected description can also be controlled by parent if desired */
  @Input() currentStatusDescription: string | null = null;
  /** Parent can control whether the status dropdown is shown (based on permissions) */
  @Input() showStatusDropdown = true;
  /** Optional override title shown in the toolbar instead of the route-derived title */
  @Input() pageTitle?: string;
  /** Optional back-link shown in the toolbar (e.g. '< Back to Document Repository') */
  @Input() backLink?: string;
  /** Label for the back link */
  @Input() backLinkLabel?: string;
  /** Optional test id applied to the back link anchor */
  @Input() backLinkTestId?: string;
  @Output() statusChange = new EventEmitter<string>();

  // Ensure the dropdown trigger displays a value as soon as parent provides items.
  ngOnChanges(changes: SimpleChanges) {
    if (changes['statusDescriptions']) {
      const items: string[] = changes['statusDescriptions'].currentValue ?? [];
      if (this.currentStatusDescription && !items.includes(this.currentStatusDescription)) {
        this.currentStatusDescription = null;
      }
    }
    if (changes['currentStatusDescription']) {
      // keep the local value in sync if parent updates it explicitly
      this.currentStatusDescription =
        changes['currentStatusDescription'].currentValue ?? this.currentStatusDescription;
    }
  }

  @ViewChild('statusDropdownRoot', { read: ElementRef }) statusDropdownRoot?: ElementRef;

  toggleStatusMenu(event?: MouseEvent) {
    if (event) event.stopPropagation();
    this.statusMenuOpen = !this.statusMenuOpen;
  }

  selectStatusDescription(value: string) {
    this.currentStatusDescription = value;
    this.statusMenuOpen = false;
    this.statusChange.emit(value);

    const legalEntityId = this.selectedLegalEntity.id();
    if (!legalEntityId) {
      return;
    }

    // Get BCR statuses and find the matching one
    this.getBcrStatusList().subscribe((statuses: BcrStatusDto[]) => {
      // First, try to find the status directly (it might be a standalone status)
      let bcrStatus = statuses.find((s: BcrStatusDto) => s.status_name === value);

      // If not found, try to construct the full name with primary part
      if (!bcrStatus) {
        const primaryPart = this.statusSection1Label();
        const fullStatusName = primaryPart ? `${primaryPart}: ${value}` : value;
        bcrStatus = statuses.find((s: BcrStatusDto) => s.status_name === fullStatusName);
      }

      if (!bcrStatus) {
        return;
      }

      const fullStatusName = bcrStatus.status_name;

      // Store the status change details for later confirmation
      this.pendingStatusChange = {
        bcrStatusId: bcrStatus.id,
        fullStatusName: fullStatusName,
        legalEntityId: legalEntityId,
      };

      // Open confirmation modal instead of making the API call immediately
      this.statusChangeModalData = {
        legalEntityName: this.selectedLegalEntity.name(),
        currentStatus: this.selectedLegalEntity.status() || '',
        newStatus: fullStatusName,
        alreadyProcessedBy: '',
      };
      this.statusChangeModalInitialState = 'confirm-change'; // Set to confirm-change for status dropdown
      this.showStatusChangeModal = true;
    });
  }

  // Called when user confirms the status change in the modal
  onConfirmStatusChange() {
    if (!this.pendingStatusChange) return;

    const { bcrStatusId, legalEntityId } = this.pendingStatusChange;

    // Get user PRID from auth session
    this.auth.getUserPrid().subscribe((prid) => {
      if (!prid) {
        this.closeStatusChangeModal();
        return;
      }

      // Get current route path to send as link with entityId query param
      const currentPath = this.currentPathSegment();
      const linkWithEntity = `${currentPath}?entityId=${legalEntityId}`;

      this.legalEntityService
        .changeBcrStatus(prid, legalEntityId, bcrStatusId, linkWithEntity)
        .subscribe({
          next: () => {
            // Don't update the status locally - it's pending approval
            // Status will only update after approval via onApproveStatusChange()
            // Show request-sent state
            if (this.statusChangeModal) {
              this.statusChangeModal.showRequestSent();
            }
          },
          error: (error) => {
            // Extract error message and details from backend
            const backendMessage =
              error.error?.message || error.message || 'Failed to change status. Please try again.';

            // Extract error details array if available
            const errorDetails = error.error?.errors || [];

            // Show error modal
            if (this.statusChangeModal) {
              this.statusChangeModal.showError(
                backendMessage,
                'Invalid Status Change',
                'error',
                errorDetails,
                'Warning',
              );
            }
          },
        });
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(ev: MouseEvent) {
    const root = this.statusDropdownRoot?.nativeElement;
    const panel = this.notificationsPanelEl?.nativeElement;
    const target = ev.target as Node;
    if (this.statusMenuOpen && root && !root.contains(target)) {
      this.statusMenuOpen = false;
    }
    if (
      this.notificationsOpen &&
      panel &&
      !panel.contains(target) &&
      !(target as HTMLElement).closest('.notification-btn')
    ) {
      this.notificationsOpen = false;
    }
    if (
      this.accountPanelOpen &&
      !(target as HTMLElement).closest('.account-panel') &&
      !(target as HTMLElement).closest('.account-btn')
    ) {
      this.accountPanelOpen = false;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape() {
    if (this.statusMenuOpen) this.statusMenuOpen = false;
    if (this.notificationsOpen) this.notificationsOpen = false;
    if (this.accountPanelOpen) this.accountPanelOpen = false;
  }

  @ViewChild('notificationsPanel') notificationsPanelEl?: ElementRef<HTMLDivElement>;

  toggleNotifications(event: MouseEvent) {
    event.stopPropagation();
    const willOpen = !this.notificationsOpen;
    if (willOpen) {
      this.accountPanelOpen = false;
      this.notificationState.loadNotifications();
      this.notificationState.clearUnreadState();
    }
    this.notificationsOpen = willOpen;
  }

  // Dynamic title logic
  private routeTitleMap: RouteTitleMap = {
    '': 'Home',
    home: 'Home',
    'document-repository': 'Document Repository',
    'legal-entity-list': 'Legal Entity List',
    'signatory-list': 'Signatory List',
    'management-tool-audit-trail': 'Management Tool Audit Trail',
    'annual-review': 'Annual Review',
    'annual-assurance-review': 'Annual Assurance Review',
    'template-repository': 'Template Repository',
  };

  currentPathSegment = signal<string>('');
  title = computed(() => {
    const seg = this.currentPathSegment();
    return this.routeTitleMap[seg] || 'Home';
  });
  isHome = computed(() => this.title() === 'Home');

  // simple signal for user full name (firstname + lastname) from auth session
  userFullName = signal<string>('');

  selectedEntityStatus = computed(() => {
    const name = this.selectedLegalEntity.name();
    if (!name) return null;
    const status = this.selectedLegalEntity.status();
    return status || null;
  });
  selectedEntityStatusLoading = computed(() => this.selectedLegalEntity.statusLoading());

  // Split status into two sections (before colon including colon, and after colon)
  statusSection1 = computed(() => {
    const full = this.selectedEntityStatus();
    if (!full) return null;
    const idx = full.indexOf(':');
    if (idx === -1) return full.trim();
    return full.slice(0, idx + 1).trim();
  });
  // Display label without trailing colon for section1 (UI only)
  statusSection1Label = computed(() => {
    const s1 = this.statusSection1();
    if (!s1) return null;
    return s1.endsWith(':') ? s1.slice(0, -1) : s1;
  });
  statusSection2 = computed(() => {
    const full = this.selectedEntityStatus();
    if (!full) return null;
    const idx = full.indexOf(':');
    if (idx === -1) return null;
    const rest = full.slice(idx + 1).trim();
    return rest ? rest : null;
  });

  // Unified selected value for interactive dropdown:
  // If there is a sub-status (section2) we use that; otherwise section1/full string.
  interactiveStatusSelected = computed(
    () => this.statusSection2() || this.statusSection1() || null,
  );

  // Convenience flag to know if we have a sub-status part
  hasSubStatus = computed(() => !!this.statusSection2());

  constructor(
    private router: Router,
    private auth: AuthService,
    private selectedLegalEntity: SelectedLegalEntityService,
    private titleService: Title,
    private notificationService: NotificationService,
    private notificationState: NotificationStateService,
    private legalEntityService: LegalEntityService,
    private signatoryService: SignatoryService,
    private logger: LoggerService,
  ) {
    // Set initial title from current URL — handles direct navigation and new-tab opens,
    // where NavigationEnd fires before the component is instantiated.
    const initialSeg = this.router.url.replace(/^\//, '').split('/')[0] || '';
    this.currentPathSegment.set(initialSeg);

    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((ev) => {
        const seg = ev.urlAfterRedirects.replace(/^\//, '').split('/')[0] || '';
        this.currentPathSegment.set(seg);
        this.updateDocumentTitle();
      });

    // attempt to populate name immediately from local storage (non-reactive fallback)
    const authData = this.auth.getAuthFromLocalStorage() as AuthLike | undefined;
    this.setNameFromAuth(authData);
    if (!this.userFullName()) {
      // proactively fetch session if toolbar loads before login route
      this.auth
        .getUserBySession()
        .pipe(filter((u: unknown) => !!u))
        .subscribe((u) => this.setNameFromAuth(u as AuthLike));
    }
    // subscribe for future session retrievals
    this.auth.currentUser$.subscribe((u: unknown) => {
      this.setNameFromAuth(u as AuthLike);
    });

    // Initial title
    this.updateDocumentTitle();

    effect(() => {
      const name = this.selectedLegalEntity.name();
      const status = this.selectedEntityStatus();
      if (!name) {
        this.currentStatusDescription = null;
        return;
      }
      if (status && this.statusDescriptions?.length) {
        const candidate = this.statusSection2() || status;
        const match = this.statusDescriptions.find((s) => s === candidate);
        if (match) this.currentStatusDescription = match;
      }
    });

    // DISABLED: Notification polling temporarily disabled per request
    // this.notificationState.startPolling();
  }

  ngAfterViewInit() {
    const el = this.toolbarWrapperEl?.nativeElement;
    if (!el || typeof ResizeObserver === 'undefined') return;
    this.resizeObserver = new ResizeObserver(() => {
      document.documentElement.style.setProperty('--toolbar-height', `${el.offsetHeight}px`);
    });
    this.resizeObserver.observe(el);
  }

  ngOnDestroy() {
    this.resizeObserver?.disconnect();
  }

  /**
   * Gets BCR status list from cache or service.
   * Returns Observable with the status list.
   */
  private getBcrStatusList(): Observable<BcrStatusDto[]> {
    // Return cached data if available
    if (this.bcrStatusCache.length > 0) {
      return of(this.bcrStatusCache);
    }

    // Fetch from service and cache the result
    return this.legalEntityService.getBcrStatuses().pipe(
      map((statuses: BcrStatusDto[]) => {
        this.bcrStatusCache = statuses;
        return statuses;
      }),
      catchError(() => of([])),
    );
  }

  private setNameFromAuth(authData: AuthLike | null | undefined) {
    if (!authData?.session?.idToken) return;
    const payload = authData.session.idToken.payload ?? {};
    const given = (payload as AuthTokenPayload).given_name ?? '';
    const last = (payload as AuthTokenPayload).middle_name ?? '';
    const full = `${given} ${last}`.trim();
    if (full) this.userFullName.set(full);
  }

  toggleAccountPanel(event: MouseEvent) {
    event.stopPropagation();
    const willOpen = !this.accountPanelOpen;
    if (willOpen) {
      this.notificationsOpen = false; // close notifications if opening account
    }
    this.accountPanelOpen = willOpen;
  }

  // Expose selected legal entity name for template (fallback to placeholder when empty)
  selectedEntityName = computed(() => {
    const n = this.selectedLegalEntity.name();
    return n || 'Select a Legal Entity';
  });

  // Only show when on legal-entity-list route AND a real entity (not placeholder) is selected.
  showSelectedEntityName = computed(() => {
    const routeSeg = this.currentPathSegment();
    // Always show placeholder while on legal-entity-list; hide entirely off-route
    return routeSeg === 'legal-entity-list';
  });

  // Status Change Modal Methods
  onReviewStatusChange(notification: NotificationDto) {
    this.currentNotification = notification;

    // Mark notification as read
    this.notificationState.markAsRead(notification.id);

    // Get legal entity name from the message
    // Format: "AstraZeneca Algeria Pharmaceutical Industries SPA status change request-approval needed."
    const legalEntityName = notification.message.split(' status change')[0];

    // Parse json_payload to get the status change details
    const payloadRaw = notification.json_payload;

    // Check if json_payload is null (already processed notification)
    if (payloadRaw === null || payloadRaw === undefined) {
      // Use the complete message from the notification
      // Messages: "The status change for AAPM SARL has been approved." or "has been rejected."
      const message = notification.message || '';
      const wasApproved = message.toLowerCase().includes('approved');
      const wasRejected = message.toLowerCase().includes('rejected');

      if (wasApproved || wasRejected) {
        // Show already-processed state immediately with the full message
        this.statusChangeModalData = {
          legalEntityName: legalEntityName,
          currentStatus: '',
          newStatus: '',
          alreadyProcessedBy: message, // Store the complete message
        };

        this.statusChangeModalInitialState = 'already-processed';
        this.showStatusChangeModal = true;
        this.notificationsOpen = false;

        return;
      }

      // Fallback: if message doesn't contain approved/rejected
      this.statusChangeModalData = {
        legalEntityName: legalEntityName,
        currentStatus: '',
        newStatus: '',
        alreadyProcessedBy: '',
      };

      this.statusChangeModalInitialState = 'review';
      this.showStatusChangeModal = true;
      this.notificationsOpen = false;
      return;
    }

    // Parse json_payload normally
    try {
      const payload = JSON.parse(payloadRaw as string);
      const newBcrStatusId = payload.new_bcr_status_id;
      const previousBcrStatusId = payload.previous_bcr_status_id;

      // Use the helper method to get status list (from cache or service)
      this.getBcrStatusList().subscribe({
        next: (statusList: BcrStatusDto[]) => {
          const currentStatusDto = statusList.find(
            (s: BcrStatusDto) => s.id === previousBcrStatusId,
          );
          const newStatusDto = statusList.find((s: BcrStatusDto) => s.id === newBcrStatusId);

          this.statusChangeModalData = {
            legalEntityName: legalEntityName,
            currentStatus: currentStatusDto?.status_name || '',
            newStatus: newStatusDto?.status_name || '',
            alreadyProcessedBy: '',
          };

          this.statusChangeModalInitialState = 'review';
          this.showStatusChangeModal = true;
          this.notificationsOpen = false;
        },
        error: () => {
          // Fallback if service call fails
          this.statusChangeModalData = {
            legalEntityName: legalEntityName,
            currentStatus: '',
            newStatus: '',
            alreadyProcessedBy: '',
          };
          this.statusChangeModalInitialState = 'review';
          this.showStatusChangeModal = true;
          this.notificationsOpen = false;
        },
      });
    } catch (error) {
      this.statusChangeModalData = {
        legalEntityName: legalEntityName,
        currentStatus: '',
        newStatus: '',
        alreadyProcessedBy: '',
      };
      this.statusChangeModalInitialState = 'review';
      this.showStatusChangeModal = true;
      this.notificationsOpen = false;
    }
  }

  onNotificationClick(notification: NotificationDto) {
    // Mark notification as read
    this.notificationState.markAsRead(notification.id);

    // Navigate to link if it exists
    if (notification.link) {
      this.router.navigateByUrl(notification.link);
      this.notificationsOpen = false; // Close notifications panel after navigation
    }
  }

  onDownloadDocument(notification: NotificationDto) {
    // Mark notification as read
    this.notificationState.markAsRead(notification.id);

    // Signatory List: use dedicated PDF download endpoint
    if (notification.notification_type === NOTIFICATION_TYPES.SIGNATORY_LIST_NEW_VERSION) {
      let filename = 'signatory_list.pdf';
      try {
        const payload = JSON.parse(notification.json_payload as string);
        filename = payload?.filename || filename;
      } catch (error) {
        this.logger.error('Error parsing signatory list notification payload:', error);
      }

      this.statusChangeModalTitle = 'Download Signatory List';
      this.statusChangeModalProcessingTitle = 'Generating download...';
      this.statusChangeModalInitialState = 'processing';
      this.showStatusChangeModal = true;

      this.signatoryService.downloadPdf().subscribe({
        next: (blob) => {
          this.signatoryService.triggerBlobDownload(blob, filename);
          if (this.statusChangeModal) {
            this.statusChangeModal.showSuccessWithMessage(
              'Your file is ready and has been downloaded.',
              'Download Complete',
            );
          }
        },
        error: (error) => {
          this.logger.error('Error downloading signatory list PDF:', error);
          if (this.statusChangeModal) {
            this.statusChangeModal.showError(
              'Failed to download the file. Please try again.',
              'Download Failed',
              'error',
            );
          }
        },
      });
      return;
    }

    // SIGNING_PROCESS_COMPLETE: download the file via download_path (authenticated request)
    if (notification.notification_type === NOTIFICATION_TYPES.SIGNING_PROCESS_COMPLETE) {
      try {
        if (!notification.json_payload) {
          this.logger.error(
            'SIGNING_PROCESS_COMPLETE notification has no json_payload',
            notification,
          );
          return;
        }
        const payload = JSON.parse(notification.json_payload as string);
        const downloadPath: string = payload?.download_path;
        const filename: string = payload?.filename || 'consolidated_signatures.pdf';
        const contentType: string = payload?.content_type || 'application/pdf';
        if (!downloadPath) {
          this.logger.error('SIGNING_PROCESS_COMPLETE payload is missing download_path', payload);
          return;
        }
        this.notificationService.downloadBase64File(downloadPath, contentType).subscribe({
          next: (blob) => {
            this.signatoryService.triggerBlobDownload(blob, filename);
          },
          error: (error) => {
            this.logger.error('Error downloading SIGNING_PROCESS_COMPLETE file:', error);
          },
        });
      } catch (error) {
        this.logger.error(
          'Error parsing SIGNING_PROCESS_COMPLETE payload:',
          error,
          notification.json_payload,
        );
      }
      return;
    }

    // Parse the json_payload to get legal_entity_id and document_file_id
    try {
      if (!notification.json_payload) {
        this.logger.error('Notification json_payload is null or undefined', notification);
        return;
      }

      const parsed = JSON.parse(notification.json_payload as string);

      // Handle both array format (LOA_FILE_REQUEST with multiple files) and object format (B_AND_CS_FILE_REQUEST, DDQ_UNDER_REVIEW)
      const payloads = Array.isArray(parsed) ? parsed : [parsed];

      // Download all files in the payload
      payloads.forEach((payload, index) => {
        if (!payload || !payload.legal_entity_id || !payload.document_file_id) {
          this.logger.error(
            `Missing legal_entity_id or document_file_id in payload at index ${index}`,
            payload,
          );
          return;
        }

        // Add a small delay between downloads to avoid overwhelming the browser
        setTimeout(() => {
          this.legalEntityService
            .generateDownloadUrl(payload.legal_entity_id, payload.document_file_id)
            .subscribe({
              next: (response) => {
                // Create a temporary anchor element to trigger download
                const link = document.createElement('a');
                link.href = response.download_url;
                link.download = response.filename || 'document';
                link.target = '_blank';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              },
              error: (error) => {
                this.logger.error('Error downloading document:', error, payload);
              },
            });
        }, index * 500); // 500ms delay between each download
      });
    } catch (error) {
      this.logger.error('Error parsing notification payload:', error, notification.json_payload);
    }
  }

  onApproveStatusChange() {
    if (!this.currentNotification) return;

    // Get approver PRID from auth session
    this.auth.getUserPrid().subscribe((approverPrid) => {
      if (!approverPrid || !this.currentNotification) {
        this.closeStatusChangeModal();
        return;
      }

      // Parse notification payload to get all required data
      try {
        const payload = JSON.parse(this.currentNotification.json_payload as string);

        // Call API to approve the status change
        this.legalEntityService
          .updateBcrStatusRequest(
            payload.id,
            payload.author,
            approverPrid,
            payload.created_date,
            'Approved',
            payload.legal_entity_id,
            payload.new_bcr_status_id,
            payload.previous_bcr_status_id,
            '', // message (optional)
            null, // last_updated_date
          )
          .subscribe({
            next: (response: ApiResponse) => {
              // Check if the status change was already processed
              if (
                response?.message &&
                response.message.toLowerCase().includes('has already been')
              ) {
                // Show already-processed state with the message from backend
                // Create new object to trigger Angular change detection
                this.statusChangeModalData = {
                  ...this.statusChangeModalData,
                  alreadyProcessedBy: response.message,
                };
                return;
              }

              // Successfully approved - show success state
              if (this.statusChangeModal) {
                this.statusChangeModal.showSuccess();
              }

              // Update the status in the dropdown and reload full entity detail
              this.getBcrStatusList().subscribe((statusList: BcrStatusDto[]) => {
                const newStatusDto = statusList.find(
                  (s: BcrStatusDto) => s.id === payload.new_bcr_status_id,
                );
                if (newStatusDto) {
                  this.selectedLegalEntity.setStatus(newStatusDto.status_name);
                  this.selectedLegalEntity.setBcrStatusId(payload.new_bcr_status_id);
                  // Request full entity detail reload to ensure all components have fresh data
                  this.selectedLegalEntity.reloadEntityDetail();
                  // Notify subscribers that status change was approved
                  this.selectedLegalEntity.notifyStatusChangeApproved();
                }
              });
            },
            error: (error) => {
              // Show error state with message from backend or default message
              const errorMessage =
                error?.error?.message ||
                error?.message ||
                'Failed to approve status change. Please try again.';
              if (this.statusChangeModal) {
                this.statusChangeModal.showError(errorMessage);
              }
            },
          });
      } catch {
        if (this.statusChangeModal) {
          this.statusChangeModal.showError('An unexpected error occurred. Please try again.');
        }
      }
    });
  }

  onRejectStatusChange() {
    if (!this.currentNotification) return;

    // Get approver PRID from auth session
    this.auth.getUserPrid().subscribe((approverPrid) => {
      if (!approverPrid || !this.currentNotification) {
        this.closeStatusChangeModal();
        return;
      }

      // Parse notification payload to get all required data
      try {
        const payload = JSON.parse(this.currentNotification.json_payload as string);

        // Call API to reject the status change
        this.legalEntityService
          .updateBcrStatusRequest(
            payload.id,
            payload.author,
            approverPrid,
            payload.created_date,
            'Rejected',
            payload.legal_entity_id,
            payload.new_bcr_status_id,
            payload.previous_bcr_status_id,
            '', // message (optional)
            null, // last_updated_date
          )
          .subscribe({
            next: (response: ApiResponse) => {
              // Check if the status change was already processed
              if (
                response?.message &&
                response.message.toLowerCase().includes('has already been')
              ) {
                // Show already-processed state with the message from backend
                // Create new object to trigger Angular change detection
                this.statusChangeModalData = {
                  ...this.statusChangeModalData,
                  alreadyProcessedBy: response.message,
                };
                return;
              }

              // Successfully rejected - show rejected state
              if (this.statusChangeModal) {
                this.statusChangeModal.showRejected();
              }
            },
            error: (error) => {
              // Show error state with message from backend or default message
              const errorMessage =
                error?.error?.message ||
                error?.message ||
                'Failed to reject status change. Please try again.';
              if (this.statusChangeModal) {
                this.statusChangeModal.showError(errorMessage);
              }
            },
          });
      } catch {
        // Error parsing payload
        if (this.statusChangeModal) {
          this.statusChangeModal.showError('An unexpected error occurred. Please try again.');
        }
      }
    });
  }

  closeStatusChangeModal() {
    this.showStatusChangeModal = false;
    this.currentNotification = null;
    this.pendingStatusChange = null;
    this.statusChangeModalTitle = 'Status Change';
    this.statusChangeModalProcessingTitle = 'Loading...';
    this.statusChangeModalData = {
      legalEntityName: '',
      currentStatus: '',
      newStatus: '',
      alreadyProcessedBy: '',
    };
  }

  private updateDocumentTitle() {
    const base = 'BCR';
    const page = this.title();
    this.titleService.setTitle(`${base} - ${page}`);
  }
}
